import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { escalateOrder } from '../../redux/slices/pendingOrdersSlice';
import { EscalationRequest, OrderPriority } from '../../types/pendingOrders';
import toast from 'react-hot-toast';
import {
  MdClose,
  MdWarning,

  MdPerson,

  MdSend,
} from 'react-icons/md';

interface OrderEscalationModalProps {
  orderId: string;
  onClose: () => void;
}

interface FormData {
  reasonId: string;
  recipientId: string;
  priority: OrderPriority;
  additionalNotes: string;
}

interface FormErrors {
  reasonId?: string;
  recipientId?: string;
  priority?: string;
  additionalNotes?: string;
}

const OrderEscalationModal: React.FC<OrderEscalationModalProps> = ({ orderId, onClose }) => {
  const dispatch = useAppDispatch();
  const { escalationReasons, escalationRecipients } = useAppSelector(state => state.pendingOrders);
  const [formData, setFormData] = useState<FormData>({
    reasonId: '',
    recipientId: '',
    priority: 'high',
    additionalNotes: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation rules
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.reasonId) {
      newErrors.reasonId = 'Please select an escalation reason';
    }

    if (!formData.recipientId) {
      newErrors.recipientId = 'Please select an escalation recipient';
    }

    if (!formData.additionalNotes.trim()) {
      newErrors.additionalNotes = 'Please provide additional notes about the escalation';
    } else if (formData.additionalNotes.trim().length < 10) {
      newErrors.additionalNotes = 'Please provide more detailed information (minimum 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the form errors before submitting');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const escalationRequest: EscalationRequest = {
        orderId,
        reasonId: formData.reasonId,
        recipientId: formData.recipientId,
        additionalNotes: formData.additionalNotes.trim(),
        priority: formData.priority
      };

      await dispatch(escalateOrder(escalationRequest)).unwrap();
      toast.success('Order escalated successfully');
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to escalate order');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedReason = escalationReasons.find(r => r.id === formData.reasonId);
  const selectedRecipient = escalationRecipients.find(r => r.id === formData.recipientId);

  const getSeverityColor = (severity: string) => {
    const colors = {
      low: 'text-green-600 bg-green-100',
      medium: 'text-yellow-600 bg-yellow-100',
      high: 'text-orange-600 bg-orange-100',
      critical: 'text-red-600 bg-red-100'
    };
    return colors[severity as keyof typeof colors] || 'text-gray-600 bg-gray-100';
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
      
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Escalate Order</h2>
              <p className="text-gray-600">Order ID: {orderId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2"
            disabled={isSubmitting}
          >
            <MdClose className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Escalation Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Escalation Reason *
              </label>
              <select
                value={formData.reasonId}
                onChange={(e) => handleInputChange('reasonId', e.target.value)}
                className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.reasonId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              >
                <option value="">Select a reason for escalation</option>
                {escalationReasons.map((reason) => (
                  <option key={reason.id} value={reason.id}>
                    {reason.name} - {reason.severity.toUpperCase()}
                  </option>
                ))}
              </select>
              {errors.reasonId && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <MdWarning className="w-4 h-4 mr-1" />
                  {errors.reasonId}
                </p>
              )}
              
              {/* Reason Details */}
              {selectedReason && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{selectedReason.name}</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(selectedReason.severity)}`}>
                      {selectedReason.severity.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{selectedReason.description}</p>
                </div>
              )}
            </div>

           

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes *
              </label>
              <textarea
                value={formData.additionalNotes}
                onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                rows={4}
                placeholder="Please provide detailed information about the issue, steps taken so far, and why escalation is needed..."
                className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.additionalNotes ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.additionalNotes ? (
                  <p className="text-red-600 text-sm flex items-center">
                    <MdWarning className="w-4 h-4 mr-1" />
                    {errors.additionalNotes}
                  </p>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Minimum 10 characters required
                  </p>
                )}
                <span className={`text-sm ${
                  formData.additionalNotes.length < 10 ? 'text-red-500' : 'text-green-600'
                }`}>
                  {formData.additionalNotes.length} characters
                </span>
              </div>
            </div>

             {/* Escalation Recipient */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Escalate To *
              </label>
              <select
                value={formData.recipientId}
                onChange={(e) => handleInputChange('recipientId', e.target.value)}
                className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.recipientId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              >
                <option value="">Select who to escalate to</option>
                {escalationRecipients.map((recipient) => (
                  <option key={recipient.id} value={recipient.id}>
                    {recipient.name} - {recipient.role}
                  </option>
                ))}
              </select>
              {errors.recipientId && (
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <MdWarning className="w-4 h-4 mr-1" />
                  {errors.recipientId}
                </p>
              )}

              {/* Recipient Details */}
              {selectedRecipient && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <MdPerson className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900">{selectedRecipient.name}</span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Role:</strong> {selectedRecipient.role}</p>
                    <p><strong>Department:</strong> {selectedRecipient.department}</p>
                    <p><strong>Email:</strong> {selectedRecipient.email}</p>
                    {selectedRecipient.phone && (
                      <p><strong>Phone:</strong> {selectedRecipient.phone}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center space-x-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Escalating...</span>
              </>
            ) : (
              <>
                <MdSend className="w-4 h-4" />
                <span>Escalate Order</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderEscalationModal;